import React, { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

const toneIcon = (tone) => {
  if (tone === 'error') return 'block';
  if (tone === 'success') return 'check_circle';
  if (tone === 'info') return 'info';
  return 'warning';
};

export function ConfirmDialog({
  open,

  title = 'Onay',
  subtitle = 'Devam etmek istiyor musun?',
  message,

  items, // [{ title, message }] veya string[]

  confirmText = 'Onayla',
  cancelText = 'Vazgeç',
  onConfirm,
  onCancel,

  tone = 'warning',
  busy = false,
  hideCancel = false,

  fullWidth = true,
  maxWidth = 'sm',

  tokens,

  // ✅ NEW: confirm butonu görünsün mü?
  showConfirm = true,
}) {
  const theme = useMuiTheme();

  const toneColor = useMemo(() => {
    const t = theme.palette?.[tone]?.main;
    return t || theme.palette.warning.main;
  }, [theme, tone]);

  const icon = toneIcon(tone);

  const list = useMemo(() => {
    if (!items) return [];
    const arr = Array.isArray(items) ? items : [];
    return arr.map((x) => {
      if (typeof x === 'string') return { title: null, message: x };
      return { title: x?.title ?? null, message: x?.message ?? '' };
    });
  }, [items]);

  const resolvedMessage = useMemo(() => {
    if (typeof message === 'function') return message();
    return message;
  }, [message]);

  return (
    <Dialog
      open={!!open}
      onClose={busy ? undefined : onCancel}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
          background: tokens?.isDark ? alpha('#0b1220', 0.92) : alpha('#ffffff', 0.98),
          border: `1px solid ${alpha(toneColor, 0.18)}`,
          boxShadow: '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
          backgroundImage: 'none',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,.45)',
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.2 }}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MDBox
            sx={{
              width: 40,
              height: 40,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              background: alpha(toneColor, 0.12),
              border: `1px solid ${alpha(toneColor, 0.22)}`,
              flex: '0 0 auto',
            }}
          >
            <Icon sx={{ color: toneColor, fontSize: 20 }}>{icon}</Icon>
          </MDBox>

          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              {title}
            </MDTypography>

            {subtitle ? (
              <MDTypography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.35 }}>
                {subtitle}
              </MDTypography>
            ) : null}
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {list.length ? (
          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {list.map((x, i) => (
              <MDBox
                key={i}
                sx={{
                  p: 1.2,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(toneColor, 0.25)}`,
                  background: alpha(toneColor, tokens?.isDark ? 0.08 : 0.06),
                }}
              >
                <MDTypography variant="button" fontWeight="bold" sx={{ display: 'block' }}>
                  {x.title || 'Bilgi'}
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.82, display: 'block', mt: 0.4 }}>
                  {x.message}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        ) : (
          <MDTypography
            variant="button"
            sx={{ opacity: 0.9, whiteSpace: 'pre-line', lineHeight: 1.5 }}
          >
            {resolvedMessage || 'Emin misin?'}
          </MDTypography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.2,
          py: 1.6,
          gap: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        {/* Cancel (her zaman olabilir) */}
        {!hideCancel ? (
          <MDButton
            variant="outlined"
            color="dark"
            onClick={onCancel}
            disabled={busy}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 800 }}
          >
            {cancelText}
          </MDButton>
        ) : null}

        {/* ✅ Confirm sadece showConfirm true ise */}
        {showConfirm ? (
          <MDButton
            variant="contained"
            color={
              tone === 'error'
                ? 'error'
                : tone === 'success'
                ? 'success'
                : tone === 'info'
                ? 'info'
                : 'warning'
            }
            onClick={onConfirm}
            disabled={busy}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
          >
            {busy ? 'İşleniyor...' : confirmText}
          </MDButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
